/**
 * Authentication utilities
 */

/**
 * Check if user is authenticated and token is not expired
 */
export function isAuthenticated() {
  const token = sessionStorage.getItem('authToken');
  if (!token) return false;

  try {
    // Decode JWT payload (no secret needed for expiry check)
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      // Token is expired — clear stale data
      logout();
      return false;
    }
    return true;
  } catch (e) {
    // Malformed token — clear it
    logout();
    return false;
  }
}

/**
 * Get auth token
 */
export function getAuthToken() {
  return sessionStorage.getItem('authToken');
}

/**
 * Get user info from sessionStorage
 */
export function getUserInfo() {
  const userInfo = sessionStorage.getItem('userInfo');
  return userInfo ? JSON.parse(userInfo) : null;
}

/**
 * Get user's assigned warehouses
 */
export function getUserWarehouses() {
  const warehouses = sessionStorage.getItem('warehouses');
  return warehouses ? JSON.parse(warehouses) : [];
}

/**
 * Check if user is a manager (can see all warehouses)
 */
export function isManager() {
  const isManagerFlag = sessionStorage.getItem('isManager');
  return isManagerFlag === 'true';
}

/**
 * Get user's role
 */
export function getRole() {
  const userInfo = getUserInfo();
  return userInfo?.role || null;
}

/**
 * Check if user is super admin (can access Control Panel)
 */
export function isAdmin() {
  return getRole() === 'super_admin';
}

/**
 * Check if user can see Control Panel > Login Activity
 * (super admins, plus the server-side allowlist carried in the token)
 */
export function canViewLoginActivity() {
  if (isAdmin()) return true;
  const token = getAuthToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.canViewLoginActivity === true;
  } catch (e) {
    return false;
  }
}

/**
 * Check if user is full admin or higher
 */
export function isFullAdmin() {
  const role = getRole();
  return role === 'super_admin' || role === 'full_admin';
}

/**
 * Logout user
 */
export function logout() {
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('userInfo');
  sessionStorage.removeItem('warehouses');
  sessionStorage.removeItem('isManager');
}

/**
 * Get axios headers with auth token
 */
export function getAuthHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Filter warehouses data based on user access
 * If user is manager/admin, return all data
 * Otherwise, filter to only show assigned warehouses
 */
export function filterWarehouseData(data, warehouseField = 'warehouse') {
  if (!data) return data;

  // Managers and admins see everything
  if (isManager() || isAdmin()) {
    return data;
  }

  // Get user's assigned warehouses
  const userWarehouses = getUserWarehouses();

  // If no warehouses assigned, return empty array
  if (!userWarehouses || userWarehouses.length === 0) {
    return Array.isArray(data) ? [] : null;
  }

  // Filter array data
  if (Array.isArray(data)) {
    return data.filter(item => {
      const itemWarehouse = item[warehouseField];
      return userWarehouses.includes(itemWarehouse);
    });
  }

  // Return single object if warehouse matches
  if (typeof data === 'object') {
    const itemWarehouse = data[warehouseField];
    return userWarehouses.includes(itemWarehouse) ? data : null;
  }

  return data;
}
